import { action, makeObservable, observable, runInAction } from "mobx";
import {
  OrderProductsVariantRequestSchema,
  OrderProductsVariantSchema,
} from "../../../models/Order/OrderProductsVariant";
import {
  ProductVariantApi,
  UpdateVariantsInput,
} from "../../../models/CatalogProducts/types";
import { OrderProductsVariantsStore } from "./OrderProductsVariantsStore";
import { CatalogProductStore } from "./CatalogProductStore";

/**
 * Local plug-and-play store for the order product variants module.
 * Do not register in stores/index.tsx — instantiate with useRef in the view.
 */
export class OrderProductVariantsModuleStore {
  orderVariantsStore: OrderProductsVariantsStore;
  catalogProductStore: CatalogProductStore;

  orderVariants: OrderProductsVariantSchema[] = [];
  catalogVariants: ProductVariantApi[] = [];
  loadingOrderVariants = false;
  loadingCatalogVariants = false;
  error: string | null = null;

  constructor() {
    this.orderVariantsStore = new OrderProductsVariantsStore();
    this.catalogProductStore = new CatalogProductStore();
    makeObservable(this, {
      orderVariants: observable,
      catalogVariants: observable,
      loadingOrderVariants: observable,
      loadingCatalogVariants: observable,
      error: observable,
      loadOrderVariants: action,
      loadCatalogVariants: action,
      loadProductDetail: action,
      createCatalogVariants: action,
      saveOrderVariant: action,
      updateOrderVariant: action,
      deleteOrderVariant: action,
    });
  }

  loadProductDetail = async (productId: number) => {
    try {
      return await this.catalogProductStore.getProductById(productId);
    } catch (error) {
      throw error;
    }
  };

  loadOrderVariants = async (
    orderId: number,
    productId: number,
    addPrQuantity: boolean = false,
  ) => {
    try {
      this.loadingOrderVariants = true;
      this.error = null;
      await this.orderVariantsStore.fetchVariantsByOrderIdAndProductId(
        orderId,
        productId,
        addPrQuantity,
      );
      runInAction(() => {
        this.orderVariants = [...this.orderVariantsStore.variants];
      });
      return this.orderVariants;
    } catch (error: any) {
      runInAction(() => {
        this.error =
          error?.response?.data?.message ||
          "Failed to load order variants. Please try again.";
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loadingOrderVariants = false;
      });
    }
  };

  loadCatalogVariants = async (productId: number) => {
    try {
      this.loadingCatalogVariants = true;
      this.error = null;
      const variants = await this.catalogProductStore.fetchVariants(productId);
      runInAction(() => {
        this.catalogVariants = (variants ?? []).filter((v) => v.active);
      });
      return this.catalogVariants;
    } catch (error: any) {
      runInAction(() => {
        this.error =
          error?.response?.data?.message ||
          "Failed to load catalog variants. Please try again.";
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loadingCatalogVariants = false;
      });
    }
  };

  createCatalogVariants = async (
    productId: number,
    newVariants: UpdateVariantsInput["variants"],
  ) => {
    try {
      const detail =
        this.catalogProductStore.productDetail?.id === productId
          ? this.catalogProductStore.productDetail
          : await this.catalogProductStore.getProductById(productId);

      const existing = (detail?.variants ?? [])
        .filter((v) => v.active)
        .map((v) => ({
          id: v.id,
          name: v.name,
          title: v.title ?? null,
          hex_code: v.hex_code ?? null,
          option_detail_ids: v.option_detail_ids ?? [],
          cost_price: v.cost_price ?? null,
          selling_price: v.selling_price ?? null,
          active: 1 as number,
        }))
        .filter((v) => (v.option_detail_ids?.length ?? 0) > 0);

      const toCreate = newVariants
        .filter((v) => (v.option_detail_ids?.length ?? 0) > 0)
        .map((v) => ({
          name: v.name,
          title: v.title ?? null,
          hex_code: v.hex_code ?? null,
          option_detail_ids: v.option_detail_ids!,
          cost_price: v.cost_price ?? null,
          selling_price: v.selling_price ?? null,
          active: 1 as number,
        }));

      if (toCreate.length === 0) {
        throw {
          response: {
            data: { message: "Select at least one new variant combination." },
          },
        };
      }

      const beforeIds = new Set(
        (detail?.variants ?? []).map((v) => v.id).filter(Boolean),
      );
      await this.catalogProductStore.updateVariants(productId, {
        variants: [...existing, ...toCreate],
      });
      const refreshed = await this.loadCatalogVariants(productId);
      const created = refreshed.filter((v) => !beforeIds.has(v.id));
      return created.length > 0 ? created : refreshed.slice(-toCreate.length);
    } catch (error) {
      throw error;
    }
  };

  saveOrderVariant = async (
    orderId: number,
    variant: OrderProductsVariantRequestSchema,
  ) => {
    try {
      await this.orderVariantsStore.addVariant(orderId, variant);
      this.orderVariants = [...this.orderVariantsStore.variants];
      return this.orderVariants;
    } catch (error) {
      throw error;
    }
  };

  updateOrderVariant = async (
    orderId: number,
    variantId: number,
    variant: OrderProductsVariantRequestSchema,
  ) => {
    try {
      await this.orderVariantsStore.updateVariant(orderId, variantId, variant);
      this.orderVariants = [...this.orderVariantsStore.variants];
      return this.orderVariants;
    } catch (error) {
      throw error;
    }
  };

  deleteOrderVariant = async (orderId: number, variantId: number) => {
    try {
      await this.orderVariantsStore.deleteVariant(orderId, variantId);
      this.orderVariants = [...this.orderVariantsStore.variants];
      return this.orderVariants;
    } catch (error) {
      throw error;
    }
  };
}
